import React from 'react';
import css from './ExplanationCard.module.scss';
import { MISSING_NAME_NOTE } from '../viewModel/publicNames';
import type { ScriptForms } from '../viewModel/example';

/**
 * Basil v0.2, P1 and P2 — the card that explains the script, on request only.
 *
 * It opens from "Show an example" and from "How this works", and from nowhere else: a writer who
 * has not asked for a lesson is not given one. It closes when they say so, not on a timer — two
 * seconds is not long enough to read a sentence, let alone four.
 *
 * Every form is spelled with THIS episode's own words, and each one says its role in words as well
 * as in colour: headings and go-to lines are the quieter pair on the page, and a reader who cannot
 * tell the two colours apart still learns what a heading is, because the card says so.
 *
 * Nothing here invites adding a scene. Adding a scene is not something this surface does, and a
 * lesson that taught it would be teaching a refusal.
 */

/** What the writer presses when they have read it. */
export const DISMISS_EXAMPLE = 'Got it';

/** The card's own heading. */
export const EXAMPLE_HEADING = 'How this script reads';

/** The one definition the brief asks for. "Suggestions" needs none; "scene" does. */
export const SCENE_DEFINITION = 'A scene is a section of your episode.';

/**
 * Choice 12, as the review seat worded it: a form this episode does not have is labelled.
 *
 * A card that silently borrows a line teaches a writer to look for names they do not have. The
 * label is one short sentence and it goes under the borrowed row, never under a row of their own.
 */
export const BORROWED_EXAMPLE = 'Example only; these names may not be in your episode.';

/**
 * T17-05: what the card says instead of an unresolved location identifier.
 *
 * "takes place in L5783328" showed a writer the engine's own spelling of a thing and then asked
 * them to reason about it. This says the same fact in ordinary words, tells them to leave the label
 * alone, and points at the one place the location itself can be looked at.
 */
export const LOCATION_NAME_UNAVAILABLE = "This scene's location name is unavailable. "
  + 'Keep its location label unchanged. Open this scene to check the location.';

/** The dialogue form: the one line form the first instruction asks a writer to change. */
export const SPEAKER_SENTENCE = 'tells you who speaks and their mood. Edit the dialogue underneath.';

/**
 * The heading form, with this episode's own scene and location.
 *
 * A scene that names no location is a real scene and reads as one: the sentence then says what the
 * heading actually does — it names the scene — rather than inventing a location to put it in.
 */
export function headingSentence(forms: ScriptForms): string {
  if (forms.locationName) {
    return ` means this scene is named ${forms.sceneName} and takes place in ${forms.locationName}. ${SCENE_DEFINITION}`;
  }

  return ` means this scene is named ${forms.sceneName}. ${SCENE_DEFINITION}`;
}

/** The answer form: what a choice line offers, and where that answer leads. */
export function choiceSentence(forms: ScriptForms): string {
  return ` offers an answer that leads to ${forms.choiceTarget}.`;
}

/** The go-to form: where the story continues when nobody is asked anything. */
export function gotoSentence(forms: ScriptForms): string {
  return ` continues the story in ${forms.gotoTarget}.`;
}

export type ExplanationCardProps = {
  /** The four forms, read off this episode's script. */
  forms: ScriptForms;
  /** Close the card and put out the highlight. */
  onDismiss: () => void;
};

/**
 * One row per form: the line as the script spells it, then what it means in ordinary words.
 */
export function ExplanationCard(props: ExplanationCardProps) {
  const { forms, onDismiss } = props;
  /** The label under a row the card had to borrow, and nothing under a row of this episode's own. */
  const borrowedLabel = (borrowed: boolean): React.ReactNode => (borrowed
    ? <span className={css.borrowed} data-testid="basil-borrowed-form">{BORROWED_EXAMPLE}</span>
    : null);

  return (
    <aside className={css.card} data-testid="basil-explanation-card" aria-label={EXAMPLE_HEADING}>
      <h2 className={css.heading}>{EXAMPLE_HEADING}</h2>
      <p className={css.row}>
        <code className={css.quiet}>{forms.heading}</code>
        {headingSentence(forms)}
        {borrowedLabel(forms.borrowed.heading)}
      </p>
      {/* T17-05: no identifier is ever shown. A name the book cannot spell is explained here, in
          ordinary words, beside the form it belongs to. */}
      {forms.unavailable.location ? (
        <p className={css.note} data-testid="basil-card-location-note">{LOCATION_NAME_UNAVAILABLE}</p>
      ) : null}
      <p className={css.row}>
        <code className={css.dialogue}>{forms.speaker}</code>
        {` ${SPEAKER_SENTENCE}`}
        {borrowedLabel(forms.borrowed.speaker)}
      </p>
      {forms.unavailable.character ? (
        <p className={css.note} data-testid="basil-card-character-note">{MISSING_NAME_NOTE.characters}</p>
      ) : null}
      {forms.unavailable.mood ? (
        <p className={css.note} data-testid="basil-card-mood-note">{MISSING_NAME_NOTE.expressions}</p>
      ) : null}
      <p className={css.row}>
        <code className={css.choice}>{forms.choice}</code>
        {choiceSentence(forms)}
        {borrowedLabel(forms.borrowed.choice)}
      </p>
      <p className={css.row}>
        <code className={css.quiet}>{forms.goto}</code>
        {gotoSentence(forms)}
        {borrowedLabel(forms.borrowed.goto)}
      </p>
      <button type="button" className={css.dismiss} onClick={onDismiss}>{DISMISS_EXAMPLE}</button>
    </aside>
  );
}
